import React, { useState } from "react";
import {
  Tabs,
  Table,
  Avatar,
  Typography,
  Tag,
  Spin,
  Card,
  Row,
  Col,
} from "antd";
import {
  UserOutlined,
  TeamOutlined,
  CustomerServiceOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  MessageOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useGetReportedUsersQuery } from "../../../redux/rtk/apiRtk";
import { motion } from "framer-motion";
import { primaryColor } from "../../../utils/constants";
import { Link } from "react-router-dom";

const { Title, Text } = Typography;

const Reports = () => {
  const [activeTab, setActiveTab] = useState("user");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // API hook
  const { data, isLoading } = useGetReportedUsersQuery({
    type: activeTab,
    page: currentPage,
    limit: pageSize,
  });

  // Handle tab change
  const handleTabChange = (key) => {
    setActiveTab(key);
    setCurrentPage(1); // Reset to first page when changing tabs
  };

  // Handle pagination
  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // Common columns for user and consultant reports
  const getUserConsultantColumns = () => [
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <ExclamationCircleOutlined style={{ color: primaryColor }} />
          </div>
          <span>ID</span>
        </div>
      ),
      dataIndex: "_id",
      key: "id",
      width: "5%",
      render: (_, __, index) => (
        <Text strong>{(currentPage - 1) * pageSize + index + 1}</Text>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <UserOutlined style={{ color: primaryColor }} />
          </div>
          <span>REPORTED {activeTab.toUpperCase()}</span>
        </div>
      ),
      dataIndex: "target",
      key: "target",
      width: "25%",
      render: (target) => (
        <div className="flex items-center">
          <Avatar
            size={40}
            src={target?.profilePic?.url || target?.profilePic || target?.image}
            icon={<UserOutlined />}
            className="border-2 border-white shadow-sm"
            style={{ boxShadow: `0 0 0 2px ${primaryColor}30` }}
          />
          <div className="ml-3">
            <Text strong className="block text-gray-800">
              {target?.firstName} {target?.lastName}
            </Text>
            <Tag
              size="small"
              className="text-xs capitalize px-2 py-0 mt-1"
              style={{
                backgroundColor: `${primaryColor}08`,
                color: primaryColor,
                border: `1px solid ${primaryColor}15`,
              }}
            >
              {target?.type}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <ExclamationCircleOutlined style={{ color: primaryColor }} />
          </div>
          <span>REPORT COUNT</span>
        </div>
      ),
      dataIndex: "reportCount",
      key: "reportCount",
      width: "15%",
      render: (count) => (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-8 h-8 rounded-full mr-2"
            style={{
              background: count > 5 ? "#ff4d4f15" : `${primaryColor}10`,
            }}
          >
            <Text
              strong
              style={{
                color: count > 5 ? "#ff4d4f" : primaryColor,
                fontSize: "12px",
              }}
            >
              {count}
            </Text>
          </div>
          <Text>{count === 1 ? "Report" : "Reports"}</Text>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <MessageOutlined style={{ color: primaryColor }} />
          </div>
          <span>LATEST MESSAGE</span>
        </div>
      ),
      dataIndex: "latestReportMessage",
      key: "latestReportMessage",
      width: "30%",
      render: (message) => (
        <div className="max-w-80">
          <Text
            className="text-sm block truncate"
            style={{ color: "#262626", fontWeight: 500 }}
            title={message}
          >
            {message || "No message"}
          </Text>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <CalendarOutlined style={{ color: primaryColor }} />
          </div>
          <span>LATEST REPORT</span>
        </div>
      ),
      dataIndex: "latestReportDate",
      key: "latestReportDate",
      width: "15%",
      render: (date) => (
        <div>
          <Text className="block">{moment(date).format("MMM Do, YYYY")}</Text>
          <Text type="secondary" className="text-xs">
            {moment(date).format("h:mm A")}
          </Text>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <EyeOutlined style={{ color: primaryColor }} />
          </div>
          <span>ACTIONS</span>
        </div>
      ),
      key: "actions",
      width: "10%",
      render: (_, record) => {
        const profilePath =
          activeTab === "consultant"
            ? `/consultant-details/${record.target._id}`
            : `/user-details/${record.target._id}`;

        return (
          <Link to={profilePath}>
            <button
              className="flex items-center px-3 py-1.5 rounded-lg text-white text-xs font-medium border-none cursor-pointer transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              style={{
                background: primaryColor,
                boxShadow: `0 2px 8px 0 ${primaryColor}40`,
              }}
            >
              <EyeOutlined
                style={{ color: "white" }}
                className="mr-1 text-xs text-white"
              />
              <p className="text-white" style={{ color: "white" }}>
                View Profile
              </p>
            </button>
          </Link>
        );
      },
    },
  ];

  // Support columns
  const getSupportColumns = () => [
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <ExclamationCircleOutlined style={{ color: primaryColor }} />
          </div>
          <span>ID</span>
        </div>
      ),
      dataIndex: "_id",
      key: "id",
      width: "5%",
      render: (_, __, index) => (
        <Text strong>{(currentPage - 1) * pageSize + index + 1}</Text>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <UserOutlined style={{ color: primaryColor }} />
          </div>
          <span>SUBMITTER</span>
        </div>
      ),
      dataIndex: "submitter",
      key: "submitter",
      width: "25%",
      render: (submitter) => (
        <div className="flex items-center">
          <Avatar
            size={40}
            icon={<UserOutlined />}
            className="border-2 border-white shadow-sm"
            style={{
              backgroundColor: `${primaryColor}50`,
              color: primaryColor,
            }}
          />
          <div className="ml-3">
            <Text strong className="block text-gray-800">
              {submitter?.firstName} {submitter?.lastName}
            </Text>
            <Tag
              size="small"
              className="text-xs capitalize px-2 py-0 mt-1"
              style={{
                backgroundColor: `${primaryColor}08`,
                color: primaryColor,
                border: `1px solid ${primaryColor}15`,
              }}
            >
              Support Request
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <MessageOutlined style={{ color: primaryColor }} />
          </div>
          <span>ISSUE</span>
        </div>
      ),
      dataIndex: "issue",
      key: "issue",
      width: "50%",
      render: (issue) => (
        <div className="max-w-96">
          <Text
            className="text-sm block truncate"
            style={{ color: "#262626", fontWeight: 500 }}
            title={issue}
          >
            {issue || "No issue description"}
          </Text>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <CalendarOutlined style={{ color: primaryColor }} />
          </div>
          <span>SUBMITTED</span>
        </div>
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      width: "20%",
      render: (date) => (
        <div>
          <Text className="block">{moment(date).format("MMM Do, YYYY")}</Text>
          <Text type="secondary" className="text-xs">
            {moment(date).format("h:mm A")}
          </Text>
        </div>
      ),
    },
  ];

  const getColumns = () => {
    return activeTab === "support"
      ? getSupportColumns()
      : getUserConsultantColumns();
  };

  const tabItems = [
    {
      key: "user",
      label: (
        <div className="flex items-center justify-between gap-5 min-w-[180px] px-2">
          <div className="flex items-center gap-3">
            <div className="text-xl flex items-center justify-center w-7 h-7">
              <UserOutlined
                className={
                  activeTab === "user" ? "text-white" : "text-gray-400"
                }
              />
            </div>
            <span
              className={`font-semibold text-base whitespace-nowrap ${
                activeTab === "user" ? "text-white" : "text-gray-400"
              }  tracking-wide`}
            >
              Users
            </span>
          </div>
          <div
            className={`px-3 py-2 rounded-2xl text-xs font-bold min-w-[36px] text-center border shadow-sm flex-shrink-0 transition-all duration-300 ${
              activeTab === "user"
                ? "bg-white text-pink-600 border-white/50"
                : "bg-white/90 border-pink-200"
            }`}
            style={{
              color: activeTab === "user" ? "white" : primaryColor,
              backgroundColor:
                activeTab === "user"
                  ? "rgba(255, 255, 255, 0.2)"
                  : "rgba(255, 255, 255, 0.9)",
              borderColor:
                activeTab === "user"
                  ? "rgba(255, 255, 255, 0.3)"
                  : `${primaryColor}25`,
            }}
          >
            {data?.stats?.user || 0}
          </div>
        </div>
      ),
    },
    {
      key: "consultant",
      label: (
        <div className="flex items-center justify-between gap-5 min-w-[180px] px-2">
          <div className="flex items-center gap-3">
            <div className="text-xl flex items-center justify-center w-7 h-7">
              <TeamOutlined
                className={
                  activeTab === "consultant" ? "text-white" : "text-gray-400"
                }
              />
            </div>
            <span
              className={`font-semibold text-base whitespace-nowrap ${
                activeTab === "consultant" ? "text-white" : "text-gray-400"
              }  tracking-wide`}
            >
              Consultants
            </span>
          </div>
          <div
            className={`px-3 py-2 rounded-2xl text-xs font-bold min-w-[36px] text-center border shadow-sm flex-shrink-0 transition-all duration-300 ${
              activeTab === "consultant"
                ? "bg-white text-pink-600 border-white/50"
                : "bg-white/90 border-pink-200"
            }`}
            style={{
              color: activeTab === "consultant" ? "white" : primaryColor,
              backgroundColor:
                activeTab === "consultant"
                  ? "rgba(255, 255, 255, 0.2)"
                  : "rgba(255, 255, 255, 0.9)",
              borderColor:
                activeTab === "consultant"
                  ? "rgba(255, 255, 255, 0.3)"
                  : `${primaryColor}25`,
            }}
          >
            {data?.stats?.consultant || 0}
          </div>
        </div>
      ),
    },
    {
      key: "support",
      label: (
        <div className="flex items-center justify-between gap-5 min-w-[180px] px-2">
          <div className="flex items-center gap-3">
            <div className="text-xl flex items-center justify-center w-7 h-7">
              <CustomerServiceOutlined
                className={
                  activeTab === "support" ? "text-white" : "text-gray-400"
                }
              />
            </div>
            <span
              className={`font-semibold text-base whitespace-nowrap ${
                activeTab === "support" ? "text-white" : "text-gray-400"
              }  tracking-wide`}
            >
              Support
            </span>
          </div>
          <div
            className={`px-3 py-2 rounded-2xl text-xs font-bold min-w-[36px] text-center border shadow-sm flex-shrink-0 transition-all duration-300 ${
              activeTab === "support"
                ? "bg-white text-pink-600 border-white/50"
                : "bg-white/90 border-pink-200"
            }`}
            style={{
              color: activeTab === "support" ? "white" : primaryColor,
              backgroundColor:
                activeTab === "support"
                  ? "rgba(255, 255, 255, 0.2)"
                  : "rgba(255, 255, 255, 0.9)",
              borderColor:
                activeTab === "support"
                  ? "rgba(255, 255, 255, 0.3)"
                  : `${primaryColor}25`,
            }}
          >
            {data?.stats?.support || 0}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="h-screen bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="h-full flex flex-col"
      >
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white shadow-sm border-b border-pink-100"
        >
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center mb-6">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl mr-4 shadow-sm bg-gradient-to-br from-pink-50 to-pink-100 border border-pink-200">
                <ExclamationCircleOutlined
                  className="text-2xl"
                  style={{ color: primaryColor }}
                />
              </div>
              <div>
                <Title
                  level={3}
                  className="m-0 font-bold"
                  style={{ color: primaryColor }}
                >
                  Reports Management
                </Title>
                <Text type="secondary" className="text-base">
                  Review and manage reported content across the platform
                </Text>
              </div>
            </div>

            {/* Enhanced Stats Cards */}
            <Row gutter={[20, 20]}>
              <Col xs={12} sm={12} md={6} lg={6}>
                <motion.div
                  whileHover={{ scale: 1.02, y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="text-center rounded-xl border border-pink-200 bg-gradient-to-br from-pink-50 to-pink-100 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                    <div className="flex flex-col items-center">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center mb-2"
                        style={{ background: primaryColor }}
                      >
                        <ExclamationCircleOutlined className="text-white text-base" />
                      </div>
                      <Text
                        strong
                        className="text-2xl font-bold"
                        style={{ color: primaryColor }}
                      >
                        {data?.stats?.total || 0}
                      </Text>
                      <Text type="secondary" className="text-sm font-medium">
                        Total Reports
                      </Text>
                    </div>
                  </Card>
                </motion.div>
              </Col>
              <Col xs={12} sm={12} md={6} lg={6}>
                <motion.div
                  whileHover={{ scale: 1.02, y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="text-center rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2 bg-blue-500">
                        <UserOutlined className="text-white text-base" />
                      </div>
                      <Text strong className="text-2xl font-bold text-blue-500">
                        {data?.stats?.user || 0}
                      </Text>
                      <Text type="secondary" className="text-sm font-medium">
                        User Reports
                      </Text>
                    </div>
                  </Card>
                </motion.div>
              </Col>
              <Col xs={12} sm={12} md={6} lg={6}>
                <motion.div
                  whileHover={{ scale: 1.02, y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="text-center rounded-xl border border-green-200 bg-gradient-to-br from-green-50 to-green-100 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2 bg-green-500">
                        <TeamOutlined className="text-white text-base" />
                      </div>
                      <Text
                        strong
                        className="text-2xl font-bold text-green-500"
                      >
                        {data?.stats?.consultant || 0}
                      </Text>
                      <Text type="secondary" className="text-sm font-medium">
                        Consultant Reports
                      </Text>
                    </div>
                  </Card>
                </motion.div>
              </Col>
              <Col xs={12} sm={12} md={6} lg={6}>
                <motion.div
                  whileHover={{ scale: 1.02, y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card
                    className="stats-card text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #faad1408 0%, #faad1415 100%)",
                      border: "1px solid #faad1420",
                      borderRadius: "12px",
                      boxShadow: "0 4px 12px #faad1415",
                    }}
                  >
                    <div className="flex flex-col items-center">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center mb-2"
                        style={{ background: "#faad14" }}
                      >
                        <CustomerServiceOutlined
                          style={{ color: "white", fontSize: "16px" }}
                        />
                      </div>
                      <Text
                        strong
                        style={{
                          color: "#faad14",
                          fontSize: "24px",
                          fontWeight: 700,
                        }}
                      >
                        {data?.stats?.support || 0}
                      </Text>
                      <Text type="secondary" className="text-sm font-medium">
                        Support Requests
                      </Text>
                    </div>
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </div>
        </motion.div>

        {/* Tabs and Content Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex-1 bg-white"
        >
          <div className="px-4 sm:px-6 lg:px-8 py-6 h-full">
            {/* Enhanced Tabs */}
            <div className="mb-6">
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 p-2 rounded-2xl shadow-inner border border-slate-200">
                <Tabs
                  activeKey={activeTab}
                  onChange={handleTabChange}
                  className="custom-reports-tabs"
                  size="large"
                  items={tabItems.map((item) => ({
                    ...item,
                    label: (
                      <motion.div
                        className="flex items-center gap-2 px-2 py-1"
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.2 }}
                      >
                        {item.label}
                      </motion.div>
                    ),
                  }))}
                />
              </div>
            </div>

            {/* Table Container */}
            <div
              className="bg-white rounded-xl shadow-sm border overflow-hidden"
              style={{
                border: `1px solid ${primaryColor}10`,
                height: "calc(100vh - 320px)",
              }}
            >
              {isLoading ? (
                <div className="flex justify-center items-center py-32">
                  <Spin
                    size="large"
                    tip={
                      <Text
                        className="mt-4 text-lg"
                        style={{ color: primaryColor }}
                      >
                        Loading {activeTab} reports...
                      </Text>
                    }
                  />
                </div>
              ) : (
                <Table
                  columns={getColumns()}
                  dataSource={data?.data || []}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: data?.pagination?.totalItems || 0,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                      `Showing ${range[0]}-${range[1]} of ${total} ${activeTab} reports`,
                    className:
                      "px-6 py-5 bg-gray-50 border-t border-gray-200 rounded-b-xl",
                    size: "default",
                  }}
                  onChange={handleTableChange}
                  className="h-full"
                  rowClassName={(__, index) =>
                    `transition-all duration-200 ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    } hover:bg-gradient-to-r hover:from-pink-50 hover:to-pink-100 hover:-translate-y-px hover:shadow-md`
                  }
                  scroll={{ x: 1200, y: "calc(100vh - 400px)" }}
                  locale={{
                    emptyText: (
                      <div className="py-16 text-center">
                        <ExclamationCircleOutlined
                          style={{
                            fontSize: 48,
                            color: "#d9d9d9",
                            marginBottom: 16,
                          }}
                        />
                        <Title level={4} type="secondary">
                          No {activeTab} reports found
                        </Title>
                        <Text type="secondary">
                          There are currently no {activeTab} reports to display.
                        </Text>
                      </div>
                    ),
                  }}
                />
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Custom Tab Styles */}
      <style jsx global>{`
        .custom-reports-tabs .ant-tabs-nav {
          margin-bottom: 0 !important;
          background: transparent !important;
          border: none !important;
        }

        .custom-reports-tabs .ant-tabs-nav-wrap {
          padding: 0 !important;
        }

        .custom-reports-tabs .ant-tabs-tab {
          background: rgba(255, 255, 255, 0.7) !important;
          border: 1px solid rgba(148, 163, 184, 0.3) !important;
          border-radius: 16px !important;
          margin: 0 8px !important;
          padding: 16px 24px !important;
          font-weight: 600 !important;
          color: #64748b !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          position: relative !important;
          overflow: hidden !important;
          backdrop-filter: blur(10px) !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
        }

        .custom-reports-tabs .ant-tabs-tab:hover {
          background: rgba(255, 255, 255, 0.9) !important;
          border-color: ${primaryColor}40 !important;
          color: ${primaryColor} !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 8px 25px rgba(164, 1, 101, 0.15) !important;
        }

        .custom-reports-tabs .ant-tabs-tab-active {
          background: linear-gradient(
            135deg,
            ${primaryColor},
            ${primaryColor}dd
          ) !important;
          border-color: ${primaryColor} !important;
          color: white !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 8px 25px rgba(164, 1, 101, 0.25) !important;
          z-index: 2 !important;
        }

        .custom-reports-tabs .ant-tabs-tab-active:hover {
          background: linear-gradient(
            135deg,
            ${primaryColor}ee,
            ${primaryColor}cc
          ) !important;
          color: white !important;
          transform: translateY(-1px) !important;
        }

        .custom-reports-tabs .ant-tabs-ink-bar {
          display: none !important;
        }

        .custom-reports-tabs .ant-tabs-tab-active .ant-typography {
          color: white !important;
        }
      `}</style>
    </div>
  );
};

export default Reports;
